/**
 * What went through the system, and what state it is all in.
 *
 * The dashboard answers "what needs me now", and empties as work is done.
 * This answers "what happened" — counts as well as amounts, month by month,
 * including the flagged debits that no live queue can still show because
 * dealing with them is exactly what takes them off it.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api, formatInr } from '../api'
import { Card, ErrorNote, Empty, Field, Loading, Note, Pill, Stat } from '../components/ui'

interface BankMonth {
  month: string
  total: number
  in_count: number
  in_paise: number
  out_count: number
  out_paise: number
  net_paise: number
  posted: number
  unposted: number
}

interface Activity {
  from_date: string
  to_date: string
  bank: BankMonth & { by_month: BankMonth[] }
  unapproved: {
    total: number
    accepted: number
    still_open: number
    posted: number
    amount_paise: number
    by_month: { month: string; flagged: number; accepted: number; open: number }[]
    lines: {
      id: string
      value_date: string
      amount_paise: number
      narration: string
      department: string | null
      accepted_by: string | null
      posted: boolean
    }[]
  }
  requests: {
    total: number
    pending: { count: number; amount_paise: number }
    approved_unpaid: { count: number; amount_paise: number }
    settled: { count: number; amount_paise: number }
    by_state: { state: string; count: number; amount_paise: number }[]
    by_month: {
      month: string
      raised: number
      approved: number
      settled: number
      amount_paise: number
    }[]
  }
  //: What each clinic banked. Six of them deposit into one account, so the
  //: amounts here come from the clinic a Book Keeper put against each credit --
  //: `amounts[i]` belongs to `clinics[i]`.
  deposits: {
    clinics: { location_id: string; code: string; name: string }[]
    by_month: {
      month: string
      amounts: number[]
      counts: number[]
      unassigned_paise: number
      unassigned_count: number
      total_paise: number
      total_count: number
    }[]
    totals: {
      amounts: number[]
      counts: number[]
      unassigned_paise: number
      unassigned_count: number
      total_paise: number
      total_count: number
    }
  }
  vendors: { party_id: string; code: string; name: string; count: number; paid_paise: number }[]
  departments: {
    department: string
    in_count: number
    in_paise: number
    out_count: number
    out_paise: number
  }[]
}

const FY_START = '2026-04-01'

export default function ActivityReport() {
  const [range, setRange] = useState({ from_date: FY_START, to_date: '' })
  const query = new URLSearchParams(
    Object.entries(range).filter(([, v]) => v !== ''),
  ).toString()
  const report = useQuery({
    queryKey: ['activity', query],
    queryFn: () => api.get<Activity>(`/reports/activity${query ? `?${query}` : ''}`),
  })

  if (report.isLoading) return <Loading what="Activity" />
  if (report.error) return <ErrorNote error={report.error} />
  const data = report.data
  if (!data) return <Empty>Nothing to report.</Empty>

  const { bank, unapproved, requests, deposits } = data
  return (
    <>
      <Card
        title="Activity"
        hint={`${data.from_date} to ${data.to_date} — counted from the books, not from a running total`}
      >
        <div className="row">
          <Field label="From">
            <input
              type="date"
              value={range.from_date}
              onChange={(e) => setRange({ ...range, from_date: e.target.value })}
            />
          </Field>
          <Field label="To" caption="blank means today">
            <input
              type="date"
              value={range.to_date}
              onChange={(e) => setRange({ ...range, to_date: e.target.value })}
            />
          </Field>
        </div>
      </Card>

      <div className="grid">
        <Stat label="Bank lines" value={bank.total} note={`${bank.posted} posted`} />
        <Stat
          label="Money in"
          value={formatInr(bank.in_paise)}
          note={`${bank.in_count} receipts`}
        />
        <Stat
          label="Money out"
          value={formatInr(bank.out_paise)}
          note={`${bank.out_count} payments`}
        />
        <Stat
          label="Left without approval"
          value={unapproved.total}
          note={
            unapproved.still_open > 0
              ? `${unapproved.still_open} still to be answered`
              : 'all since accepted'
          }
        />
        <Stat
          label="Requests pending"
          value={requests.pending.count}
          note="anywhere in the company, not just yours"
        />
        <Stat
          label="Approved, unpaid"
          value={formatInr(requests.approved_unpaid.amount_paise)}
          note={`${requests.approved_unpaid.count} requests`}
        />
      </div>

      <Card title="Month by month" hint="bank lines, by the date the money moved">
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Lines</th>
              <th className="num">In</th>
              <th className="num">Received</th>
              <th className="num">Out</th>
              <th className="num">Paid</th>
              <th className="num">Net</th>
              <th className="num">Posted</th>
            </tr>
          </thead>
          <tbody>
            {bank.by_month.map((m) => (
              <tr key={m.month}>
                <td className="date">{m.month}</td>
                <td className="num">{m.total}</td>
                <td className="num">{m.in_count}</td>
                <td className="num">{formatInr(m.in_paise)}</td>
                <td className="num">{m.out_count}</td>
                <td className="num">{formatInr(m.out_paise)}</td>
                <td className="num">{formatInr(m.net_paise)}</td>
                <td className="num">
                  {m.posted}
                  {m.unposted > 0 && <span className="faint"> / {m.unposted} not</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card
        title="Deposits by clinic"
        hint="credits only, by the date the money reached the bank — posted or not, so this agrees with Received above"
      >
        {deposits.clinics.length === 0 ? (
          <Empty>No clinic is set up yet.</Empty>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Month</th>
                  {deposits.clinics.map((c) => (
                    <th key={c.location_id} className="num">
                      {c.name}
                    </th>
                  ))}
                  <th className="num">Unassigned</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {deposits.by_month.map((m) => (
                  <tr key={m.month}>
                    <td className="date">{m.month}</td>
                    {deposits.clinics.map((c, i) => (
                      <td key={c.location_id} className="num">
                        {m.amounts[i] > 0 ? formatInr(m.amounts[i]) : <span className="faint">—</span>}
                      </td>
                    ))}
                    <td className="num">
                      {/* Never hidden, never guessed at, never split across the
                          clinics. Money nobody has claimed is a question for
                          somebody, and a report that quietly dropped it would be
                          worse than useless. */}
                      {m.unassigned_paise > 0 ? (
                        <strong>{formatInr(m.unassigned_paise)}</strong>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                    <td className="num">{formatInr(m.total_paise)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>Total</td>
                  {deposits.clinics.map((c, i) => (
                    <td key={c.location_id} className="num">
                      {formatInr(deposits.totals.amounts[i])}
                    </td>
                  ))}
                  <td className="num">{formatInr(deposits.totals.unassigned_paise)}</td>
                  <td className="num">{formatInr(deposits.totals.total_paise)}</td>
                </tr>
              </tfoot>
            </table>
            {deposits.totals.unassigned_paise > 0 && (
              <Note kind="warn">
                {formatInr(deposits.totals.unassigned_paise)} across{' '}
                {deposits.totals.unassigned_count}{' '}
                {deposits.totals.unassigned_count === 1 ? 'deposit' : 'deposits'} has no clinic
                against it, so it counts in the total but belongs to nobody. Set the clinic on
                Bank statements.
              </Note>
            )}
          </>
        )}
      </Card>

      <Card
        title="Money that left without an approved request"
        hint="kept after they are dealt with — the live queue cannot show you these"
      >
        {unapproved.total === 0 ? (
          <Empty>None in this period.</Empty>
        ) : (
          <>
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th className="num">Amount</th>
                  <th>What</th>
                  <th>Department</th>
                  <th>Accepted by</th>
                  <th>Posted</th>
                </tr>
              </thead>
              <tbody>
                {unapproved.lines.map((line) => (
                  <tr key={line.id}>
                    <td className="date">{line.value_date}</td>
                    <td className="num">{formatInr(line.amount_paise)}</td>
                    <td className="muted">{line.narration}</td>
                    <td>{line.department ?? <span className="faint">—</span>}</td>
                    <td>
                      {line.accepted_by ?? <Pill value="unapproved" />}
                    </td>
                    <td>{line.posted ? 'yes' : <span className="faint">not yet</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="muted">
              {unapproved.total} in this period, {formatInr(unapproved.amount_paise)} in all.{' '}
              {unapproved.accepted} accepted, {unapproved.still_open} still open.
            </p>
          </>
        )}
      </Card>

      <Card
        title="Payment requests"
        hint="by the date the money was needed, not the day the row was typed"
      >
        <table>
          <thead>
            <tr>
              <th>Month</th>
              <th className="num">Raised</th>
              <th className="num">Approved, unpaid</th>
              <th className="num">Settled</th>
              <th className="num">Value</th>
            </tr>
          </thead>
          <tbody>
            {requests.by_month.map((m) => (
              <tr key={m.month}>
                <td className="date">{m.month}</td>
                <td className="num">{m.raised}</td>
                <td className="num">{m.approved}</td>
                <td className="num">{m.settled}</td>
                <td className="num">{formatInr(m.amount_paise)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="muted">
          {requests.by_state.map((s) => `${s.count} ${s.state.replace(/_/g, ' ')}`).join(' · ')}
        </p>
      </Card>

      <div className="grid-2">
        <Card title="Paid, by vendor" hint="from settled requests, which know who they paid">
          {data.vendors.length === 0 ? (
            <Empty>Nothing settled yet.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Vendor</th>
                  <th className="num">Payments</th>
                  <th className="num">Paid</th>
                </tr>
              </thead>
              <tbody>
                {data.vendors.map((v) => (
                  <tr key={v.party_id}>
                    <td>
                      {v.name} <span className="faint mono">{v.code}</span>
                    </td>
                    <td className="num">{v.count}</td>
                    <td className="num">{formatInr(v.paid_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="By department" hint="bank lines that carry one">
          {data.departments.length === 0 ? (
            <Empty>No line carries a department yet.</Empty>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Department</th>
                  <th className="num">Lines</th>
                  <th className="num">Spent</th>
                </tr>
              </thead>
              <tbody>
                {data.departments.map((d) => (
                  <tr key={d.department}>
                    <td>{d.department}</td>
                    <td className="num">{d.out_count}</td>
                    <td className="num">{formatInr(d.out_paise)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>
    </>
  )
}
